import React, { useMemo } from 'react';
import { StripeAddresssField } from '@components/stripe/stripe-address-field';
import { useOrganization } from '@context/OrganizationContext';
import {
  useSubscriptions,
  useSubscriptionsDispatch,
} from '@features/subscriptions/context/subscription-context';
import { useGetPaymentMethods } from '@features/subscriptions/hooks/useGetPaymentMethods';
import { useUpdateSubscriptionAddons } from '@features/subscriptions/hooks/useUpdateSubscriptionAddons';
import { Alert, Button, Divider, FlexView, Loading, Text } from '@goodfynd/ui';
import { renderPrice } from '@utils/number-util';

import { SubscriptionCheckoutItemList } from './SubscriptionCheckoutItemList';
import { SubscriptionCheckoutPaymentMethods } from './SubscriptionCheckoutPaymentMethods';

import classes from './SubscriptionsCheckout.module.css';

export function SubscriptionCheckout() {
  const { organization, organizationId } = useOrganization();
  const { activeSubscriptions, activeSuite, cart, cartSubtotal } =
    useSubscriptions();
  const { findBySku, resetSubscriptions, setPaymentMethod } =
    useSubscriptionsDispatch();
  const { updateSubscriptionAddons } = useUpdateSubscriptionAddons(
    organizationId,
    activeSuite?.subscriptionId,
    {
      onSuccess() {
        resetSubscriptions();
      },
    }
  );

  const {
    defaultPaymentMethod,
    paymentMethods = [],
    isFetching: isFetchingPaymentMethods,
  } = useGetPaymentMethods(organizationId, {
    enabled: !!organization?.stripeSaasCustomerId,
    onSuccess(data) {
      if (!data[0]) {
        return;
      }

      const defaultPaymentMethod =
        data.find((paymentMethod) => paymentMethod.isDefault) ?? data[0];
      setPaymentMethod(defaultPaymentMethod.id);
    },
  });

  const isTrialEligible = activeSubscriptions[0]?.isTrialEligible ?? false;

  const { hasTrialItems, trialTotal, trialPeriodDays } = useMemo(() => {
    if (!isTrialEligible) {
      return { hasTrialItems: false, trialTotal: 0, trialPeriodDays: 0 };
    }

    let total = 0;
    let maxTrialDays = 0;
    let found = false;

    for (const item of cart.items) {
      const product = findBySku(item.sku);
      const price = product?.prices.find((p) => p.frequency === cart.frequency);

      if (price?.trialPeriod) {
        found = true;
        total += price.price * item.newQuantity;
        maxTrialDays = Math.max(maxTrialDays, price.trialPeriod);
      }
    }

    return {
      hasTrialItems: found,
      trialTotal: total,
      trialPeriodDays: maxTrialDays,
    };
  }, [cart.frequency, cart.items, findBySku, isTrialEligible]);

  const dueToday = hasTrialItems ? cartSubtotal - trialTotal : cartSubtotal;

  if (isFetchingPaymentMethods) {
    return <Loading />;
  }

  return (
    <FlexView
      className={classes.container}
      direction="column"
      gap={32}
      width="100%"
      wrap="nowrap"
    >
      <SubscriptionCheckoutItemList />

      {hasTrialItems ? (
        <FlexView direction="column" gap={16} width="100%">
          <FlexView gap={16} justify="space-between" width="100%">
            <Text as="sectionHeader">Due today</Text>
            <Text as="sectionHeader">{renderPrice(dueToday)}</Text>
          </FlexView>

          <Divider />

          <FlexView direction="column" gap={4} width="100%">
            <FlexView gap={16} justify="space-between" width="100%">
              <Text as="body2">After trial</Text>
              <Text as="body2">
                {renderPrice(cartSubtotal)}/
                {cart.frequency === 'annual' ? 'year' : 'month'}
              </Text>
            </FlexView>

            <Text as="body2" color="TextIconSecondary">
              {trialPeriodDays}-day free trial included
            </Text>
          </FlexView>
        </FlexView>
      ) : (
        <FlexView gap={16} justify="space-between" width="100%">
          <Text as="sectionHeader">Subtotal</Text>
          <Text as="sectionHeader">{renderPrice(cartSubtotal)}</Text>
        </FlexView>
      )}

      {!organization?.location && (
        <StripeAddresssField
          onChange={(address) => console.log('Address changed:', address)}
        />
      )}

      {activeSuite && cartSubtotal > 0 && (
        <SubscriptionCheckoutPaymentMethods
          defaultPaymentMethod={defaultPaymentMethod}
          savedPaymentMethods={paymentMethods}
        />
      )}

      {!activeSuite && (
        <Alert status="info">
          <Text as="body2">
            You are not currently on a subscription. Please select a suite first
            to continue.
          </Text>
        </Alert>
      )}

      <Button
        async
        content="Place order"
        disabled={!activeSuite || (cartSubtotal > 0 && !cart.paymentMethodId)}
        fullWidth
        onClick={async () => {
          await updateSubscriptionAddons(
            cart.items.map((item) => ({
              priceId: item.priceId,
              quantity: item.quantity,
            })),
            cart.paymentMethodId
          );
        }}
      />
    </FlexView>
  );
}
